import { AnimatePresence } from "framer-motion";
import { Wifi, Check } from "lucide-react";
import { ConnectionStatus } from "@/features/tutor/store/useConnectionStore";
import { StatusToast } from "@/components/StatusToast";
import { useState, useEffect, useRef } from "react";

const WifiWithCheck = () => (
  <div className="relative">
    <Wifi className="w-5 h-5 text-[#4CAF50]" />
    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#4CAF50] rounded-full flex items-center justify-center">
      <Check className="w-2 h-2 text-white" strokeWidth={3} />
    </div>
  </div>
);

const WifiUnstable = () => (
  <div className="relative">
    <Wifi className="w-5 h-5 text-[#F59E0B]" />
    <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[#F59E0B] rounded-full flex items-center justify-center">
      <span className="text-[8px] font-bold text-white leading-none">!</span>
    </div>
  </div>
);

interface ConnectionToastProps {
  status: ConnectionStatus;
  wasEverDisconnected: boolean;
}

const ConnectionToast = ({
  status,
  wasEverDisconnected,
}: ConnectionToastProps) => {
  const [showBackOnline, setShowBackOnline] = useState(false);
  const prevStatusRef = useRef<ConnectionStatus>(status);
  const backOnlineTimerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    const wasDisconnected =
      prevStatusRef.current === ConnectionStatus.DISCONNECTED;
    const isNowConnected = status === ConnectionStatus.CONNECTED;

    if (wasDisconnected && isNowConnected && wasEverDisconnected) {
      setShowBackOnline(true);

      if (backOnlineTimerRef.current) {
        clearTimeout(backOnlineTimerRef.current);
      }

      backOnlineTimerRef.current = setTimeout(() => {
        setShowBackOnline(false);
        backOnlineTimerRef.current = null;
      }, 3000);
    }

    prevStatusRef.current = status;

    return () => {
      if (backOnlineTimerRef.current) {
        clearTimeout(backOnlineTimerRef.current);
      }
    };
  }, [status, wasEverDisconnected]);

  const handleDismissBackOnline = () => {
    setShowBackOnline(false);
    if (backOnlineTimerRef.current) {
      clearTimeout(backOnlineTimerRef.current);
      backOnlineTimerRef.current = null;
    }
  };

  return (
    <AnimatePresence mode="wait">
      {status === ConnectionStatus.DISCONNECTED && (
        <StatusToast
          key="disconnected"
          variant="warning"
          icon={<WifiUnstable />}
          className="w-full"
        >
          Your internet connection is unstable
        </StatusToast>
      )}

      {showBackOnline && status === ConnectionStatus.CONNECTED && (
        <StatusToast
          key="back-online"
          variant="success"
          icon={<WifiWithCheck />}
          onDismiss={handleDismissBackOnline}
          className="w-full"
        >
          EverTutor is back online
        </StatusToast>
      )}
    </AnimatePresence>
  );
};

export default ConnectionToast;
